import React, { useState, useEffect } from 'react';

// Import showcase images
import dashboardOverview from '../../assets/showcase/dashboard_overview.png';
import aiAdvisor from '../../assets/showcase/ai_advisor.png';
import aiExtraction from '../../assets/showcase/ai_extraction.png';
import investmentPlanner from '../../assets/showcase/investment_planner.png';
import scenarioPlanner from '../../assets/showcase/scenario_planner.png';
import ranking from '../../assets/showcase/leaderboard.png';

const showcaseItems = [
  {
    title: 'Smart Portfolio Dashboard',
    description: 'Real-time valuations and equity tracking at your fingertips.',
    extendedDescription: 'Your command center for property wealth. Watch your equity grow in real-time with automated valuation updates, track rental yields across your entire portfolio, and get instant visibility into your net worth. With read-only access for your Buyer Agent or Accountant, you can collaborate with your professional team while maintaining total control of your data.',
    image: dashboardOverview,
  },
  {
    title: 'AI Portfolio Advisor',
    description: 'Your 24/7 intelligent assistant for complex financial queries.',
    extendedDescription: 'Meet your personal AI strategist. Ask anything from "What\'s my best-performing property?" to "How will rate rises affect my cash flow?" Our proprietary AI understands your portfolio deeply and delivers institutional-grade insights in plain English.',
    image: aiAdvisor,
  },
  {
    title: 'Automated Document Extraction',
    description: 'Stop manual entry. Let AI handle your settlement statements.',
    extendedDescription: 'Drop in a PDF, get back data. Our neural network reads settlement statements, bank statements, and invoices with 99%+ accuracy. Extraction happens in seconds, creating an accountant-ready trail that makes tax time a breeze. Grant read-only access to your accountant to automate your yearly reconciliation without lifting a finger.',
    image: aiExtraction,
  },
  {
    title: 'Investment Strategy Wizard',
    description: 'Build your multi-property roadmap with precision.',
    extendedDescription: 'From first investment to financial freedom. Answer a few questions about your goals, risk tolerance, and timeline—our AI builds a personalized acquisition roadmap. Know exactly when to buy, what to buy, and how to structure your portfolio for maximum growth.',
    image: investmentPlanner,
  },
  {
    title: 'Scenario Analysis',
    description: 'Model future acquisitions and interest rate impacts.',
    extendedDescription: 'What if you bought that apartment? What if rates hit 7%? Stop guessing and start modelling. Stress-test your portfolio against any scenario—rate hikes, vacancy periods, renovation costs. Make confident decisions backed by real numbers.',
    image: scenarioPlanner,
  },
  {
    title: 'Property Ranking',
    description: 'Rank your properties by performance and identify top performers.',
    extendedDescription: 'Which property is your star performer? See all your assets ranked side-by-side by yield, growth, cash flow, and overall ROI. Instantly spot underperformers, identify optimization opportunities, and make data-driven decisions about where to focus your attention.',
    image: ranking,
  }
];

const PlatformShowcase: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<{ title: string; image: string; extendedDescription: string } | null>(null);

  // Close modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedImage) {
        setSelectedImage(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-brand-secondary mb-4">
            See CanvasEstate in Action
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            A visual overview of the tools and insights powering thousands of smart property decisions across Australia.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {showcaseItems.map((item, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-2xl transition-all duration-500 border border-gray-100 cursor-pointer"
              onClick={() => setSelectedImage({ title: item.title, image: item.image, extendedDescription: item.extendedDescription })}
            >
              {/* Image Container */}
              <div className="aspect-[16/10] overflow-hidden relative">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                />
                {/* Zoom Icon Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3 shadow-lg">
                    <svg className="w-6 h-6 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Overlay Content */}
              <div className="p-6 bg-white transition-colors duration-300 group-hover:bg-brand-primary">
                <h4 className="text-lg font-bold text-gray-900 group-hover:text-white mb-1 transition-colors">
                  {item.title}
                </h4>
                <p className="text-sm text-gray-500 group-hover:text-blue-100 transition-colors leading-relaxed">
                  {item.description}
                </p>
              </div>

              {/* Hover Glow Effect */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/20 rounded-2xl transition-all duration-500 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Visual Decoration */}
        <div className="mt-16 flex justify-center">
          <p className="text-sm text-gray-400">Click on any image to view full size</p>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-6xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors flex items-center gap-2"
            >
              <span className="text-sm">Close</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image */}
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />

            {/* Title and Description */}
            <div className="mt-6 text-center max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-white mb-3">{selectedImage.title}</h3>
              <p className="text-gray-300 text-lg leading-relaxed">{selectedImage.extendedDescription}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PlatformShowcase;
